<template>
  <div>
    <!-- Invoice table -->
    <grid-details-component
      v-if="setupFormDone"
      :display_spec="invoiceSpecs"
      :item="invoiceForm"
      :item_keys="invoice_keys">
    </grid-details-component>
    <!-- Invoice articles table -->
    <table-component
      v-if="articles && setupFormDone"
      ref="articleTable"
      :content="articles"
      :headings="table_headings"
      :item_keys="invoice_article_keys"
      :display_spec="invoiceArticleSpecs"
      class="veridens-dashboard">
    </table-component>
    <!-- Loader -->
    <loader-component v-else :position="'relative'"></loader-component>
    <!-- Action buttons -->
    <submit-btn-component
      class="submit-btn is-uppercase is-outlined is-primary is-large is-pulled-right"
      @submit="saveInvoice($event, InvoiceState.PUBLISH)" :group="'invoice'" v-translate>
      Confirm
    </submit-btn-component>
    <submit-btn-component
      class="submit-btn is-uppercase is-outlined is-warning is-large is-pulled-right"
      @submit="saveInvoice($event, InvoiceState.DRAFT)" :group="'invoice'" v-translate>
      Save as Draft
    </submit-btn-component>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import { formHandlerMixin } from '@/mixins/mixins'
  import TableComponent from '@/components/layout/table/TableComponent'
  import LoaderComponent from '@/components/layout/utility/LoaderComponent'
  import GridDetailsComponent from '@/components/layout/grid/GridDetailsComponent'
  import SubmitBtnComponent from '@/components/layout/utility/SubmitBtnComponent'
  import FormData from '@/classes/form/FormData'
  import { invoiceArticleSpecs, invoiceSpecs } from '@/display_context/display_context'
  import { Invoice, InvoiceArticle, InvoiceState } from '@/classes/case/Invoice'
  import Status from '@/classes/http/Status'
  import { SERVICE_UNAVAILABLE_MESSAGE } from '@/utility/errorMessages'

  export default {
    name: 'invoice-form-component',
    mixins: [formHandlerMixin],
    components: {
      TableComponent,
      LoaderComponent,
      GridDetailsComponent,
      SubmitBtnComponent
    },
    props: {
      kase: {
        type: Object,
        required: false
      },
      invoice: {
        type: Object,
        required: false
      }
    },
    data () {
      return {
        invoice_keys: ['markingField', 'kiwaReferenceField', 'customerReferenceField'],
        table_headings: ['Article No.', 'Description', 'Price', 'Qty', 'Sum', ''],
        invoice_article_keys: ['numberField', 'descriptionField', 'priceField', 'quantityField', 'sum', 'button'],
        invoiceSpecs: invoiceSpecs,
        invoiceArticleSpecs: invoiceArticleSpecs,
        InvoiceState: InvoiceState,
        invoiceForm: {},
        articles: [],
        setupFormDone: false,
        total: {
          value: 0,
          customClasses: 'has-text-weight-bold',
          colspan: 2
        }
      }
    },
    computed: {
      ...mapState({
        cases: state => state.caseModule.cases,
        invoices: state => state.invoiceModule.invoices,
        formDescriptors: state => state.invoiceModule.invoiceFormDescriptors,
        formDatas: state => state.invoiceModule.invoiceFormDatas,
        articleNumberFormDescriptors: state => state.invoiceModule.articleNumberFormDescriptors,
        articleNumberFormDatas: state => state.invoiceModule.articleNumberFormDatas,
        articleFormDescriptors: state => state.invoiceModule.articleFormDescriptors,
        articleFormDatas: state => state.invoiceModule.articleFormDatas
      }),
      formDescriptor () {
        return this.formDescriptors[0]
      },
      formData () {
        return this.formDatas[0]
      },
      articleFormDescriptor () {
        return this.articleFormDescriptors[0]
      },
      articleFormData () {
        return this.articleFormDatas[0]
      },
      articleNumberFormDescriptor () {
        return this.articleNumberFormDescriptors[0]
      },
      articleNumberFormData () {
        return this.articleNumberFormDatas[0]
      },
      isCreateMode () {
        return !!this.kase
      },
      isEditMode () {
        return !!this.invoice
      },
      connectCaseId () {
        return this.$route.query.connectCaseId
      }
    },
    methods: {
      ...mapActions([
        'setupInvoiceCreateFormForCase',
        'setupInvoiceForm',
        'setupArticlesListCreateForm',
        'setupArticlesListEditForm',
        'setupArticlesListFormForCase',
        'setupArticleAddForm',
        'setupArticleRowForm',
        'postInvoiceCreateForm',
        'putInvoiceEditForm',
        'putInvoiceArticleEditForm',
        'postConfirmInvoice',
        'successMessage'
      ]),
      /***************************************************************
       * Methods for saving and updating the invoice and its articles
       ***************************************************************/
      saveInvoice (btn, state) {
        if (this.isCreateMode) {
          this.createInvoice(btn, state)
        } else if (this.isEditMode) {
          this.updateInvoice(btn, state)
        }
      },
      createInvoice (btn, state) {
        let requestData = {
          formDatas: this.formDatas,
          caseId: this.kase.id
        }
        this.mixin.callbackData = { btn: btn, state: state }
        this.submitRequest = this.postInvoiceCreateForm
        this.submitForm(requestData, btn, this.saveInvoiceCallback)
      },
      updateInvoice (btn, state) {
        let requestData = {
          formDatas: this.formDatas,
          invoiceId: this.invoice.id
        }
        this.mixin.callbackData = { btn: btn, state: state }
        this.submitRequest = this.putInvoiceEditForm
        this.submitForm(requestData, btn, this.saveInvoiceCallback)
      },
      saveInvoiceCallback (response, callbackData) {
        callbackData.btn.disable()
        let invoiceId = response.data && response.data.id
        this.saveInvoiceArticles(invoiceId, callbackData.btn, callbackData.state)
      },
      saveInvoiceArticles (invoiceId, btn, state) {
        let requestData = {
          formDatas: this.articleFormDatas,
          invoiceId: invoiceId
        }
        if (this.connectCaseId) {
          requestData.caseIds = [this.connectCaseId]
        }
        this.mixin.callbackData = { invoiceId: invoiceId, state: state, btn: btn }
        this.submitRequest = this.putInvoiceArticleEditForm
        this.submitForm(requestData, btn, this.saveInvoiceArticlesCallback)
      },
      saveInvoiceArticlesCallback (response, callbackData) {
        if (callbackData.state === InvoiceState.DRAFT) {
          this.$router.push({ name: 'InvoiceEditView', params: { invoiceId: callbackData.invoiceId }, query: {} })
          if (this.isCreateMode) {
            this.successMessage('The invoice was created successfully!')
          } else if (this.isEditMode) {
            this.successMessage('The invoice was updated successfully!')
          }
        } else if (callbackData.state === InvoiceState.PUBLISH) {
          this.confirmInvoice(callbackData.invoiceId, callbackData.btn)
        }
      },
      confirmInvoice (invoiceId, btn) {
        btn.disable()
        this.postConfirmInvoice(invoiceId).then(() => {
          this.$router.push({ name: 'InvoiceDetailsView', params: { invoiceId: invoiceId }, query: {} })
          this.successMessage('The invoice was published successfully!')
        }).catch((err) => {
          this.handleError(err)
        }).finally(() => {
          btn.enable()
        })
      },
      /**************************************************
       * Common methods
       **************************************************/
      createFields (form, obj, fieldParams, fieldSetIndex) {
        for (let param of fieldParams) {
          let field = form.descriptor.fieldSets[fieldSetIndex].getFieldForUID(param.uid)
          field.fieldSetIndex = fieldSetIndex
          if (param.uid === InvoiceArticle.NUMBER_UID) {
            field.properties.input_readonly = true
          }
          obj[param.prop] = {
            field: field,
            formData: form.data,
            showLabel: !!param.showLabel
          }
          if (param.callback) {
            FormData.addCallback(form.data, field, param.callback)
          }
        }
      },
      /**************************************************
       * Methods for creating the invoice articles form
       **************************************************/
      addArticle () {
        let field = this.articleNumberFormDescriptor.fieldSets[0].getFieldForUID(InvoiceArticle.NUMBER_UID)
        let value = this.articleNumberFormData.getValueForField(field)
        if (value) {
          this.setupArticleRowForm(value).then(() => {
            let articleRow = this.createInvoiceArticleFormField(this.articleFormDescriptor.fieldSets.length - 1)
            this.articles.push(articleRow)
            this.calculateTotal()
          }).catch((err) => {
            let response = err.response || {}
            if (response.status === Status.SERVICE_UNAVAILABLE) {
              this.errorMessage(SERVICE_UNAVAILABLE_MESSAGE)
              return
            }
            let msg = response.data && response.data.detail
            this.articleNumberFormData.handleInvalidResponse([{ uid: field.uid, message: msg, data_group: 'native' }])
          })
        }
      },
      calculateArticleSum (article) {
        let price = this.articleFormData.getValueForField(article.priceField.field)
        let qty = this.articleFormData.getValueForField(article.quantityField.field)
        article.sum = price && qty ? (price * qty) : 0
      },
      calculateArticleTotal (field) {
        let article = this.articles.find(article => {
          return article.priceField.field === field || article.quantityField.field === field
        })
        this.calculateArticleSum(article)
        this.calculateTotal()
      },
      calculateTotal () {
        let total = 0
        this.articles.forEach(article => {
          total += parseFloat(article.sum)
        })
        this.total.value = total
      },
      createArticleActionRow () {
        let dynamicField = this.articleNumberFormDescriptor.fieldSets[0].getFieldForUID(InvoiceArticle.NUMBER_UID)
        // update the id so that id doesn't collide with already existing ones
        dynamicField.updateId(this.articleNumberFormData, dynamicField.id + 'dynamic')
        // add the dynamic row for adding an article
        this.$refs.articleTable.dynamic_rows = []
        this.$refs.articleTable.dynamic_rows.push({
          cells: [
            {
              component: 'FormFieldComponent',
              value: {
                field: dynamicField,
                formData: this.articleNumberFormData,
                showLabel: false
              }
            },
            {
              component: 'ButtonComponent',
              value: {
                text: this.$gettext('ADD ARTICLE'),
                clickHandler: this.addArticle,
                customClasses: 'is-success is-pulled-left'
              },
              colspan: 5
            }
          ]
        })
        this.$refs.articleTable.dynamic_rows.push({
          cells: [
            {
              value: 'Total:',
              colspan: 4,
              customClasses: 'is-pulled-right has-text-weight-bold'
            },
            this.total
          ]
        })
        this.calculateTotal()
      },
      clearFormDataForArticle (article) {
        for (let key in this.articleFormData.fields) {
          for (let articleKey in article) {
            if (article[articleKey].field && key === article[articleKey].field.id) {
              this.articleFormData.removeFieldForId(key)
            }
          }
        }
      },
      removeArticleRow (row) {
        let article = this.articles.splice(row.index, 1)[0]
        this.clearFormDataForArticle(article)
        this.calculateTotal()
      },
      createInvoiceArticleFormField (fieldSetIndex) {
        let article = {}
        let form = {
          descriptor: this.articleFormDescriptor,
          data: this.articleFormData
        }
        const fieldParams = [
          {
            uid: InvoiceArticle.NUMBER_UID,
            prop: 'numberField'
          },
          {
            uid: InvoiceArticle.DESCRIPTION_UID,
            prop: 'descriptionField'
          },
          {
            uid: InvoiceArticle.PRICE_UID,
            prop: 'priceField',
            callback: this.calculateArticleTotal
          },
          {
            uid: InvoiceArticle.QUANTITY_UID,
            prop: 'quantityField',
            callback: this.calculateArticleTotal
          }
        ]
        this.createFields(form, article, fieldParams, fieldSetIndex)
        // add remove button
        article.button = {
          clickHandler: this.removeArticleRow,
          type: 'remove',
          customClasses: 'is-outlined'
        }
        this.calculateArticleSum(article)
        return article
      },
      createInvoiceArticleFormFields () {
        let articles = []
        this.articleFormDescriptor.fieldSets.forEach((fieldSet, fieldSetIndex) => {
          let articleRow = this.createInvoiceArticleFormField(fieldSetIndex)
          articles.push(articleRow)
        })
        this.articles = articles
      },
      /**************************************************
       * Methods for creating the invoice form
       **************************************************/
      createInvoiceFormFields () {
        let form = {
          descriptor: this.formDescriptor,
          data: this.formData
        }
        const fieldParams = [
          {
            uid: Invoice.MARKING_UID,
            prop: 'markingField',
            showLabel: true
          },
          {
            uid: Invoice.KIWA_REFERENCE_UID,
            prop: 'kiwaReferenceField',
            showLabel: true
          },
          {
            uid: Invoice.CUSTOMER_REFERENCE_UID,
            prop: 'customerReferenceField',
            showLabel: true
          }
        ]
        if (this.isEditMode) {
          this.invoice_keys.unshift('stateCell')
          this.invoice_keys.push('modified')
          this.invoiceForm.modified = this.invoice.modified
          this.invoiceForm.stateCell = this.invoice.state
        }
        this.createFields(form, this.invoiceForm, fieldParams, 0)
      },
      /**************************************************
       * Getting the form templates
       **************************************************/
      setupArticleFormsForCase () {
        // if there is no connected case ignore it
        if (!this.connectCaseId) {
          return new Promise((resolve) => {
            return resolve()
          })
        }
        // if there is a connected case retrieve and add its invoice articles
        return this.setupArticlesListFormForCase(this.connectCaseId)
      },
      setupArticleForms () {
        this.setupArticleFormsForCase().then(() => {
          this.setupArticleAddForm().then(() => {
            this.createInvoiceFormFields()
            this.createInvoiceArticleFormFields()
            this.setupFormDone = true
            this.$nextTick(() => {
              this.createArticleActionRow()
            })
          }).catch((err) => {
            this.handleError(err)
          })
        })
      },
      setupFormsByKase () { // used when creating a new invoice
        this.setupInvoiceCreateFormForCase(this.kase.id).then(() => {
          if (!this.setupFormDone) {
            this.setupArticlesListCreateForm(this.kase.id).then(() => {
              this.setupArticleForms()
            }).catch((err) => {
              this.handleError(err)
            })
          }
        }).catch((err) => {
          this.handleError(err)
        })
      },
      setupFormsByInvoice () { // used when updating an already existing invoice
        this.setupInvoiceForm(this.invoice.id).then(() => {
          if (!this.setupFormDone) {
            this.setupArticlesListEditForm(this.invoice.id).then(() => {
              this.setupArticleForms()
            }).catch((err) => {
              this.handleError(err)
            })
          }
        }).catch((err) => {
          this.handleError(err)
        })
      }
    },
    created () {
      if (this.isCreateMode) {
        this.setupFormsByKase()
      } else if (this.isEditMode) {
        this.setupFormsByInvoice()
      }
    }
  }
</script>

<style lang="sass" scoped>
  .form-field-set
    margin-bottom: 27px
  .form-field
    margin-bottom: 0 !important
  .submit-btn
    margin-left: 1rem
</style>
